import logger from "@/lib/logger";

/**
 * The short-lived, SINGLE-READ handoff between "a user clicked a card in a feed" and "the discussion
 * page recorded a view".
 *
 * <p>`PageViewTracker` runs on the discussion page and has no idea where the reader came from, so the
 * origin has to be recorded at click time and picked up one navigation later. `sessionStorage` rather
 * than an in-memory value because the card click is frequently a full document navigation.
 *
 * <p>Deliberately ONE slot, not a map keyed by discussion: a map grows without bound across a session
 * and needs its own eviction policy, while a reader can only have come from one place. The stored id is
 * still checked on read, so a stale entry left by an abandoned click never attributes the wrong page.
 *
 * <p>Absent is a real, meaningful state. When there is no origin — a hard navigation, a refresh, an
 * external arrival — the tracker sends nothing and the server stores `NULL`, which reads as
 * "unattributed" rather than as a false `direct`. Collapsing those two is the single easiest way to make
 * this data lie: the unattributed share is large at first and shrinks as call sites are wired up, which
 * would otherwise look like an organic decline in direct traffic. See
 * designs/01-now/2026-07-30-feed-discovery-instrumentation-design.md §3.3.
 */

const STORAGE_KEY = "seavien.analytics.feed-click-origin.v1";

/** An origin older than this is stale — the user wandered off and came back by some other route. */
const MAX_ORIGIN_AGE_MS = 5 * 60 * 1000;

export type FeedClickOrigin = {
    discussionPublicId: string;
    source: string;
    sourceDetail?: string;
};

type StoredFeedClickOrigin = FeedClickOrigin & { at: number };

export function recordFeedClickOrigin(origin: FeedClickOrigin): boolean {
    if (typeof window === "undefined" || !origin.discussionPublicId || !origin.source) {
        return false;
    }

    try {
        const stored: StoredFeedClickOrigin = { ...origin, at: Date.now() };
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        return true;
    } catch (error) {
        // A full or unavailable sessionStorage must never break a navigation. Losing the attribution
        // degrades the row to NULL, which is exactly the state the design already handles.
        logger.debug("[analytics] Failed to record the feed click origin.", error);
        return false;
    }
}

/**
 * Read the origin for this discussion and clear it, whatever the outcome. Single-read is the point: a
 * refresh of the same discussion page must not re-attribute the second view to the feed.
 */
export function consumeFeedClickOrigin(discussionPublicId: string): FeedClickOrigin | null {
    if (typeof window === "undefined" || !discussionPublicId) {
        return null;
    }

    let rawValue: string | null = null;
    try {
        rawValue = window.sessionStorage.getItem(STORAGE_KEY);
        if (rawValue != null) {
            window.sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch (error) {
        logger.debug("[analytics] Failed to read the feed click origin.", error);
        return null;
    }

    if (!rawValue) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawValue) as Partial<StoredFeedClickOrigin> | null;
        if (!parsed || typeof parsed !== "object") {
            return null;
        }

        if (parsed.discussionPublicId !== discussionPublicId) {
            return null;
        }

        if (typeof parsed.at !== "number" || Date.now() - parsed.at > MAX_ORIGIN_AGE_MS) {
            return null;
        }

        if (typeof parsed.source !== "string" || parsed.source.length === 0) {
            return null;
        }

        return {
            discussionPublicId: parsed.discussionPublicId,
            source: parsed.source,
            sourceDetail: typeof parsed.sourceDetail === "string" && parsed.sourceDetail.length > 0
                ? parsed.sourceDetail
                : undefined,
        };
    } catch (error) {
        logger.debug("[analytics] Failed to parse the feed click origin.", error);
        return null;
    }
}

export function clearFeedClickOriginForTests(): void {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignored — tests only
    }
}
